import React, { useEffect, useRef, useState } from "react";
import { QrCode, Download, Printer } from "lucide-react";
import { toast } from "sonner";
import { Modal, Button } from "@/components/common";
import { labelImageDataUrl, downloadBarcodeJpg, printLabels } from "@/lib/barcode";

// Inline barcode preview + per-item actions, shown under an SKU field.
export function BarcodeInline({ item }) {
  const [url, setUrl] = useState("");
  const [err, setErr] = useState("");
  const sku = (item?.sku || "").trim();
  useEffect(() => {
    if (!sku) { setUrl(""); setErr(""); return; }
    try { setUrl(labelImageDataUrl(item)); setErr(""); }
    catch { setUrl(""); setErr("Kode tidak valid"); }
  }, [sku, item?.name, item?.price]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!sku) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-2 flex flex-col items-center gap-2" data-testid="barcode-inline">
      {url ? <img src={url} alt={`Barcode ${sku}`} className="h-24 object-contain" /> : <div className="text-xs text-rose-500 py-3">{err || "Membuat barcode…"}</div>}
      <div className="flex gap-2 w-full">
        <Button size="sm" variant="outline" className="flex-1" onClick={() => downloadBarcodeJpg(item, item.name || sku)} data-testid="barcode-download-jpg"><Download size={14} /> Unduh JPG</Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={() => printLabels([item], "thermal")} data-testid="barcode-print-one"><Printer size={14} /> Cetak Label</Button>
      </div>
    </div>
  );
}

// Modal to print ALL barcodes of a list (A4 sheet or thermal 33x15mm).
export function BarcodePrintModal({ open, onClose, items, title = "Cetak Barcode" }) {
  const [mode, setMode] = useState("a4");
  const list = items || [];
  const valid = list.filter((i) => i.sku && String(i.sku).trim());
  const missing = list.length - valid.length;
  const doPrint = () => {
    const n = printLabels(valid, mode);
    if (n === -1) return toast.error("Popup diblokir browser. Izinkan popup lalu coba lagi.");
    if (n === 0) return toast.error("Tidak ada item ber-barcode untuk dicetak.");
    toast.success(`Menyiapkan ${n} label untuk dicetak…`);
    onClose?.();
  };
  if (!open) return null;
  return (
    <Modal open onClose={onClose} title={title} maxWidth="max-w-md">
      <div className="space-y-4">
        <p className="text-sm text-slate-500"><b>{valid.length}</b> item punya barcode{missing > 0 ? ` · ${missing} item belum punya SKU (dilewati)` : ""}.</p>
        <div className="space-y-2">
          <span className="text-sm font-medium text-slate-600">Format cetak</span>
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${mode === "a4" ? "border-primary bg-primary/5" : "border-slate-200"}`}>
            <input type="radio" name="bc-mode" checked={mode === "a4"} onChange={() => setMode("a4")} className="mt-1" data-testid="bc-mode-a4" />
            <div><div className="font-medium text-slate-800">Lembar A4 (banyak label)</div><div className="text-xs text-slate-500">±40 label per halaman A4. Pilih "Simpan sebagai PDF" di dialog cetak untuk menyimpan file.</div></div>
          </label>
          <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer ${mode === "thermal" ? "border-primary bg-primary/5" : "border-slate-200"}`}>
            <input type="radio" name="bc-mode" checked={mode === "thermal"} onChange={() => setMode("thermal")} className="mt-1" data-testid="bc-mode-thermal" />
            <div><div className="font-medium text-slate-800">Label thermal 33 × 15 mm</div><div className="text-xs text-slate-500">1 label per halaman (3 baris: nama, barcode, SKU · harga) untuk printer label thermal.</div></div>
          </label>
        </div>
        <Button className="w-full" onClick={doPrint} disabled={!valid.length} data-testid="bc-print-go"><Printer size={16} /> Cetak / Simpan PDF</Button>
      </div>
    </Modal>
  );
}

export { QrCode };
